// Palette + emoji catalogue for class customization. Mirrors iOS ClassTheme.
// `color` is a palette KEY ("teal", …) stored on the backend, so iOS and web
// tint identically. Class strings are written out in full so Tailwind's content
// scanner keeps them (dynamic `bg-${x}` would be purged).
import { cx } from "@/components/ui";

export const CLASS_PALETTE = [
  "blue", "teal", "green", "orange", "purple", "pink", "red", "indigo", "gray",
] as const;
export type ClassColorKey = (typeof CLASS_PALETTE)[number];

const TILE: Record<string, string> = {
  blue: "bg-blue-100 text-blue-700",
  teal: "bg-teal-100 text-teal-700",
  green: "bg-green-100 text-green-700",
  orange: "bg-orange-100 text-orange-700",
  purple: "bg-purple-100 text-purple-700",
  pink: "bg-pink-100 text-pink-700",
  red: "bg-red-100 text-red-700",
  indigo: "bg-indigo-100 text-indigo-700",
  gray: "bg-gray-100 text-gray-700",
};

const SWATCH: Record<string, string> = {
  blue: "bg-blue-500",
  teal: "bg-teal-500",
  green: "bg-green-500",
  orange: "bg-orange-500",
  purple: "bg-purple-500",
  pink: "bg-pink-500",
  red: "bg-red-500",
  indigo: "bg-indigo-500",
  gray: "bg-gray-500",
};

const DEFAULT_TILE = "bg-brand-100 text-brand-700";

export function classTile(key?: string | null): string {
  return (key && TILE[key]) || DEFAULT_TILE;
}
export function classSwatch(key: string): string {
  return SWATCH[key] || "bg-brand-500";
}

export const CLASS_EMOJIS = [
  "🎓", "📚", "🧪", "⚗️", "🔬", "🧮", "📐", "🎨", "🎭", "⚽️", "🎵",
  "🌍", "💻", "📖", "✏️", "🏫", "🧠", "🔭", "🗺️", "📝", "🎯", "🧬",
];
export const DEFAULT_CLASS_EMOJI = "🎓";

/** Full role label (member rows). Mirrors iOS displayRole. */
export function roleLabel(role: string): string {
  switch (role) {
    case "owner": return "Teacher";
    case "admin": return "Teaching Assistant";
    case "member": return "Student";
    default: return role;
  }
}
/** Compact role label (list rows). */
export function shortRoleLabel(role: string): string {
  switch (role) {
    case "owner": return "Teacher";
    case "admin": return "TA";
    case "member": return "Student";
    default: return role;
  }
}

/** Emoji-on-tint class chip. */
export function ClassIcon({
  emoji,
  color,
  size = 40,
}: {
  emoji?: string | null;
  color?: string | null;
  size?: number;
}) {
  return (
    <span
      className={cx("flex shrink-0 items-center justify-center rounded-2xl", classTile(color))}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.5) }}
    >
      {emoji || DEFAULT_CLASS_EMOJI}
    </span>
  );
}
